import { Card, DataList, EmptyView, ErrorView, ListControls, LoadingView, StatusBadge, DashStyles as Styles } from '../../components/dashboard/DashboardUI';
import { FeedbackMessage } from '../../components/dashboard/FeedbackMessage';
import { usePaginatedList } from '../../components/dashboard/usePaginatedList';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { Button } from '@app/engine';
import { CommonConstants, FormatTimestamp, Logger, WrapLTR } from '@shared/common';
import { CallClient, IntegrationClient, Telemetry } from '@shared/frontend';
import { useState } from 'react';
import type { IFeedbackDTO } from '../../components/dashboard/FeedbackMessage';
import type { INotificationDTO } from '@shared/common';

/** File-local constants. */
const localConstants = {
  logContext: 'Notifications',
  // Succeeded notifications have landed, so no resend is offered; pending is shown but disabled; failed/needs_review enable resend.
  doneStatuses: [CommonConstants.Database.ActionStatus.Succeeded] as string[],
  busyStatuses: [CommonConstants.Database.ActionStatus.Pending] as string[]
} as const;

/**
 * Notifications screen: SMS and email messages dispatched on the tenant's behalf, one bubble per message (dashboard data layout). A bubble whose delivery has not succeeded offers a same-size
 * Resend control beside its status pill (disabled while still in flight, hidden once delivered).
 *
 * @returns The notifications element.
 */
export function Notifications() {
  const { tenantID } = useDashboard();
  const list = usePaginatedList(
    (signal, offset, limit, pattern) => IntegrationClient.ListNotifications(signal, tenantID!, offset, limit, pattern),
    (signal, pattern) => IntegrationClient.CountNotifications(signal, tenantID!, pattern),
    [tenantID]
  );
  const [busyID, setBusyID] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<IFeedbackDTO | null>(null);

  const resend = async (notification: INotificationDTO) => {
    if (!notification.callID) return;
    setBusyID(notification.id);
    setFeedback(null);
    try {
      const controller = new AbortController();
      await CallClient.ResendSMS(controller.signal, tenantID!, notification.callID);
      Telemetry.NotificationResent(notification.channel);
      setFeedback({ text: Localized.NotificationsResendDone, type: 'success' });
      list.reload();
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      setFeedback({ text: Localized.DashError, type: 'error' });
    } finally {
      setBusyID(null);
    }
  };

  // Status row: delivery pill plus a same-size Resend pill-button for messages not yet succeeded (disabled while in flight, hidden once delivered, only when the call is known).
  const statusValue = (notification: INotificationDTO) => {
    const delivered = localConstants.doneStatuses.includes(notification.status);
    const inFlight = localConstants.busyStatuses.includes(notification.status);
    return (
      <>
        <StatusBadge status={notification.status} />
        {!delivered && notification.callID ? (
          <Button
            text={Localized.NotificationsResend}
            disabled={inFlight || busyID === notification.id}
            classNameButtonEnabled={`${Styles.btn} ${Styles.btnPill}`}
            classNameButtonDisabled={`${Styles.btn} ${Styles.btnPill}`}
            onClickHandler={() => void resend(notification)}
          />
        ) : null}
      </>
    );
  };

  return (
    <>
      <ListControls
        searchValue={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={Localized.NotificationsSearchPlaceholder}
        offset={list.offset}
        limit={list.limit}
        total={list.total}
        pageCount={list.data.length}
        onOffsetChange={list.setOffset}
        onLimitChange={list.setLimit}
        loading={list.loading}
      />
      <Card title={Localized.NotificationsTitle} titleHint={Localized.NotificationsHint} feedback={<FeedbackMessage feedback={feedback} />}>
        {list.loading ? (
          <LoadingView />
        ) : list.error ? (
          <ErrorView onRetry={list.reload} />
        ) : list.data.length === 0 ? (
          <EmptyView message={Localized.NotificationsNone} />
        ) : (
          <div className={Styles.cardStack}>
            {list.data.map((notification) => (
              <div key={notification.id} className={Styles.subPanel}>
                <DataList
                  rows={[
                    { label: Localized.NotificationsChannel, value: notification.channel },
                    { label: Localized.SharedTo, value: WrapLTR(notification.toAddress) },
                    { label: Localized.NotificationsBody, value: notification.body },
                    { label: Localized.SharedStatus, value: statusValue(notification) },
                    { label: Localized.SharedWhen, value: FormatTimestamp(notification.createdAt) }
                  ]}
                />
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
